import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Building2, Pencil, Plus, Search } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Checkbox } from '@/components/ui/Input';
import {
  EmptyState,
  Pagination,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from '@/components/ui/Table';
import { SectionSpinner } from '@/components/ui/Spinner';
import { Can } from '@/app/guards/RoleGuard';
import { shopsApi } from '@/services/endpoints/masterData';
import { queryKeys } from '@/services/queryKeys';
import { formatMoney } from '@/domain/money';
import { ShopFormModal } from '../components/ShopFormModal';
import type { Shop } from '@/domain/types';

export function ShopsPage(): JSX.Element {
  const { t } = useTranslation();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [withOutstandingOnly, setWithOutstandingOnly] = useState(false);
  const [editing, setEditing] = useState<Shop | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);

  const params = { q: search || undefined, page, pageSize: 25, unassignedOnly, withOutstandingOnly };

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.shops.list(params),
    queryFn: () => shopsApi.list(params),
    // Keeps the previous page visible while the next loads, so the table does
    // not collapse to a spinner on every keystroke.
    placeholderData: (previous) => previous,
  });

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (shop: Shop) => {
    setEditing(shop);
    setFormOpen(true);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('shop.title')}
        actions={
          <Can permission="shops:create">
            <Button onClick={openCreate} leftIcon={<Plus className="h-4 w-4" />}>
              {t('shop.new')}
            </Button>
          </Can>
        }
      />

      <Card>
        <div className="flex flex-wrap items-center gap-4 border-b border-slate-200 p-4">
          <div className="relative min-w-56 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder={t('common.searchPlaceholder')}
              className="h-10 w-full rounded-lg border border-slate-300 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
          </div>

          <Checkbox
            label={t('shop.unassignedOnly')}
            checked={unassignedOnly}
            onChange={(event) => {
              setUnassignedOnly(event.target.checked);
              setPage(1);
            }}
          />

          <Checkbox
            label={t('shop.withOutstandingOnly')}
            checked={withOutstandingOnly}
            onChange={(event) => {
              setWithOutstandingOnly(event.target.checked);
              setPage(1);
            }}
          />
        </div>

        {isLoading ? (
          <SectionSpinner />
        ) : !data || data.rows.length === 0 ? (
          <EmptyState
            icon={<Building2 className="h-6 w-6" />}
            title={t('shop.empty')}
            action={
              <Can permission="shops:create">
                <Button onClick={openCreate} leftIcon={<Plus className="h-4 w-4" />}>
                  {t('shop.new')}
                </Button>
              </Can>
            }
          />
        ) : (
          <>
            <Table>
              <THead>
                <TR>
                  <TH>{t('shop.code')}</TH>
                  <TH>{t('shop.clientName')}</TH>
                  <TH>{t('shop.contactNumber')}</TH>
                  <TH>{t('shop.route')}</TH>
                  <TH>{t('shop.agent')}</TH>
                  <TH align="right">{t('shop.outstanding')}</TH>
                  <TH align="center">{t('common.status')}</TH>
                  <TH align="right">{t('common.actions')}</TH>
                </TR>
              </THead>

              <TBody>
                {data.rows.map((shop) => (
                  <TR key={shop.id}>
                    <TD className="font-mono text-xs text-slate-500">{shop.code}</TD>
                    <TD className="font-medium text-slate-900">{shop.name}</TD>
                    <TD>{shop.contactNumber ?? '—'}</TD>
                    <TD>
                      {shop.routeName ?? (
                        // A shop with no route reaches no agent — worth calling out.
                        <Badge tone="warning">{t('shop.noRoute')}</Badge>
                      )}
                    </TD>
                    <TD>{shop.agentName ?? '—'}</TD>
                    <TD align="right" numeric>
                      {shop.outstandingCents > 0 ? (
                        <span className="text-slate-900">{formatMoney(shop.outstandingCents)}</span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </TD>
                    <TD align="center">
                      <Badge tone={shop.isActive ? 'success' : 'neutral'}>
                        {shop.isActive ? t('common.active') : t('common.inactive')}
                      </Badge>
                    </TD>
                    <TD align="right">
                      <Can permission="shops:update">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(shop)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </Can>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>

            <Pagination meta={data.meta} onPageChange={setPage} />
          </>
        )}
      </Card>

      <ShopFormModal isOpen={isFormOpen} onClose={() => setFormOpen(false)} shop={editing} />
    </div>
  );
}
